import { useEffect, useState } from 'react';

type DocxSource = Blob | ArrayBuffer;

type DocxSemantic = {
  /** Ruwe tekst van het hele document (mammoth extractRawText) — zoekindex. */
  rawText: string;
  /** Semantische HTML met custom style mappings (mammoth convertToHtml) — verrijking. */
  html: string;
};

type DocxProcessorStatus = 'idle' | 'processing' | 'ready' | 'error';

// Spec: Word-highlights → <mark>, comment-references → <sup>.
const STYLE_MAP = ['highlight => mark', 'comment-reference => sup'];

/**
 * Centrale documentverwerking: leest de bron exact één keer in als ArrayBuffer
 * en fan-out't parallel naar docx-preview (visuele render in `container`) en
 * mammoth (rawText + semantische HTML). Beide libraries worden lazy geladen.
 */
export function useDocxProcessor(
  source: DocxSource | null | undefined,
  container: HTMLElement | null,
) {
  const [status, setStatus] = useState<DocxProcessorStatus>('idle');
  const [semantic, setSemantic] = useState<DocxSemantic | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!source || !container) return;

    let cancelled = false;
    setStatus('processing');
    setSemantic(null);
    setError(null);

    (async () => {
      try {
        // NFR: het document wordt exact één keer in het geheugen gelezen.
        const buffer = source instanceof Blob ? await source.arrayBuffer() : source;
        if (cancelled) return;

        // Lazy chunks: geen van beide libraries wordt geladen vóór een .docx opent.
        const [docxPreview, mammoth] = await Promise.all([
          import('docx-preview'),
          import('mammoth'),
        ]);
        if (cancelled) return;

        // docx-preview muteert zijn target-element intern zodra renderAsync is
        // aangeroepen — een cancelled promise die alsnog resolvet zou de live
        // container van een nieuwer document overschrijven. Render daarom in een
        // losstaand element en verplaats de nodes pas als deze run nog geldig is.
        const offscreen = document.createElement('div');

        const [, rawTextResult, htmlResult] = await Promise.all([
          docxPreview.renderAsync(buffer, offscreen, undefined, {
            className: 'docx-preview',
            inWrapper: true,
            // Default true; false zodat door Word ingevoegde pagina-einden pagineren.
            ignoreLastRenderedPageBreak: false,
          }),
          mammoth.extractRawText({ arrayBuffer: buffer }),
          mammoth.convertToHtml({ arrayBuffer: buffer }, { styleMap: STYLE_MAP }),
        ]);
        if (cancelled) return;

        // docx-preview appendt zonder te clearen; vorige render altijd eerst wissen.
        container.replaceChildren(...offscreen.childNodes);
        setSemantic({ rawText: rawTextResult.value, html: htmlResult.value });
        setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source, container]);

  return { status, semantic, error };
}

export type { DocxProcessorStatus, DocxSemantic, DocxSource };
